import React, { useState, useEffect } from 'react';
import Modal from './Modal';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AgentEmailModal = ({ open, onClose, onSubmit, agent, gmUsername = []}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [selectedGM, setSelectedGM] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail(agent?.email || '');
    setRole(agent?.role || ''); // Reset or set initial role if available
    setSelectedGM(agent?.AttachedGM || '');
  }, [open, agent]);

  // Validation logic
  const emailValid = !email || emailRegex.test(email);
  const isJuniorManager = role === 'JM';
  const roleValid = !isJuniorManager || (isJuniorManager && !!selectedGM);
  
  const canSubmit = emailValid && roleValid && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await onSubmit(agent.name, email || null, role || null, isJuniorManager ? selectedGM : null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Set Details for ${agent?.name || 'Agent'}`}>
      <div className="space-y-4">
        {/* Agent Name Field */}
        <div>
          <label htmlFor="agent-name" className="block text-sm font-medium text-gray-700 mb-1">
            Agent Name
          </label>
          <input
            id="agent-name"
            className="input-field bg-gray-100"
            type="text"
            value={agent?.name || ''}
            disabled
          />
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="agent-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            id="agent-email"
            className="input-field"
            type="email"
            placeholder="agent@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {!emailValid && email && (
            <p className="mt-1 text-sm text-red-600" aria-live="polite">
              Please enter a valid email address.
            </p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Leave empty to remove the email address. The agent will not receive individual performance reports.
          </p>
        </div>

        {/* Role Selection Field */}
        <div>
          <label htmlFor="agent-role" className="block text-sm font-medium text-gray-700 mb-1">
            Role
          </label>
          <select
            id="agent-role"
            className="input-field bg-white"
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              if (e.target.value !== 'JM') setSelectedGM(''); // Clear GM if role changes
            }}
          >
            <option value="">Select a role (Optional)</option>
            <option value="GM">General Manager</option>
            <option value="JM">Junior Manager</option>
          </select>
        </div>

      {/* GM Selection - Only shows if Junior Manager is selected */}
        {isJuniorManager && (
          <div className="animate-fade-in">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign to General Manager <span className="text-red-500">*</span>
            </label>
            <select
              className={`input-field bg-white ${!selectedGM ? 'border-red-300' : ''}`}
              value={selectedGM}
              onChange={(e) => setSelectedGM(e.target.value)}
            >
              <option value="">-- Select a GM --</option>
              {gmUsername.map((gm) => (
                <option key={gm.name} value={gm.name}>
                  {gm.name} {gm.email ? `(${gm.email})` : ''}
                </option>
              ))}
            </select>
            {gmUsername.length === 0 && (
              <p className="mt-1 text-xs text-red-500 italic">
                No General Managers found. You must create a GM first.
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-2 pt-2">
          <button onClick={onClose} className="btn-outline" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="btn-primary disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AgentEmailModal;